import axios from 'axios';
import { API_BASE_URL, getUploadsBaseUrl, sanitizeAuthToken } from './apiService';

export interface DashboardStats {
  totalSales: number;
  totalPurchaseOrders: number;
  totalProducts: number;
  totalRevenue: number;
  salesGrowth: number;
  revenueGrowth: number;
  poCount: number;
}

export interface RecentTransaction {
  id: number;
  type: 'sale' | 'purchase';
  amount: number;
  date: string;
  status: string;
}

export interface DashboardData {
  stats: DashboardStats;
  recentTransactions: RecentTransaction[];
}

// Use a local axios instance with proper timeout to avoid global error toasts from apiService interceptors.
const API_BASE = API_BASE_URL;

// Create axios instance with increased timeout for Render free tier cold starts
const dashboardAxios = axios.create({
  baseURL: API_BASE,
  timeout: 60000, // 60 seconds for Render free tier
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
dashboardAxios.interceptors.request.use((config) => {
  const token = sanitizeAuthToken(localStorage.getItem('authToken'));
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const isDeployedBackend = /\.onrender\.com|\.vercel\.app|\.railway\.app/i.test(API_BASE_URL);

let wakePromise: Promise<void> | null = null;
/** Ping /health to wake Render (and similar) backends before heavy dashboard requests. Only used when API is deployed. */
export function wakeBackend(): Promise<void> {
  if (!isDeployedBackend) return Promise.resolve();
  if (wakePromise) return wakePromise;
  const base = getUploadsBaseUrl();
  const url = `${base.replace(/\/+$/, '')}/health`;
  wakePromise = new Promise<void>((resolve) => {
    const timeout = new Promise<void>((r) => setTimeout(r, 28000));
    const request = fetch(url, { method: 'GET' }).then(() => {}).catch(() => {});
    Promise.race([request, timeout]).then(resolve);
  });
  return wakePromise;
}

export const dashboardService = {
  wakeBackend,

  // Get dashboard statistics from real API
  async getDashboardStats(): Promise<DashboardData> {
    try {
      // Stats + inventory + recent txns only (yesterday totals come from /dashboard/stats — avoids 2 heavy POS list queries)
      const [statsRes, invRes, recentTxns] = await Promise.all([
        dashboardAxios.get<any>('/dashboard/stats'),
        dashboardAxios.get<any>('/inventory-views/dashboard/inventory-overview').catch(() => ({ data: {} })),
        dashboardAxios.get<any[]>('/pos/transactions', { params: { limit: 10 } }),
      ]);

      const statsRaw = statsRes.data || {};
      const todayRevenue = Number(statsRaw.total_sales ?? 0);
      const revenueGrowth = Number(
        statsRaw.revenue_growth ?? 0,
      );
      const salesGrowth = Number(statsRaw.sales_growth ?? 0);

      const totalProducts = Number(invRes?.data?.stock_summary?.total_products || 0);

      const recentTransactions: RecentTransaction[] = (recentTxns.data || []).map((t: any) => ({
        id: Number(t.id),
        type: 'sale',
        amount: Number(t.total_amount || 0),
        date: String(t.sale_date || ''),
        status: String((t.status || 'completed')).toLowerCase(),
      }));

      return {
        stats: {
          // Map backend dashboard stats into the existing UI fields
          totalSales: Number(statsRaw.total_orders ?? 0),
          totalPurchaseOrders: Number(statsRaw.low_stock || 0), // shown as "Low stock" in UI
          totalProducts,
          totalRevenue: todayRevenue,
          salesGrowth: Number.isFinite(salesGrowth) ? +Number(salesGrowth).toFixed(1) : 0,
          revenueGrowth: Number.isFinite(revenueGrowth) ? +Number(revenueGrowth).toFixed(1) : 0,
          poCount: Number(statsRaw.po_count ?? 0),
        },
        recentTransactions,
      };
    } catch (error) {
      console.warn('Backend API not available, using fallback data:', error instanceof Error ? error.message : 'Unknown error');
      // Fallback to demo data when backend is not available
      return {
        stats: {
          totalSales: 245,
          totalPurchaseOrders: 32,
          totalProducts: 156,
          totalRevenue: 450675.50,
          salesGrowth: 12.5,
          revenueGrowth: 8.3,
          poCount: 0,
        },
        recentTransactions: [
          {
            id: 1,
            type: 'sale' as const,
            amount: 1200.00,
            date: '2024-01-20',
            status: 'completed',
          },
          {
            id: 2,
            type: 'purchase' as const,
            amount: 5600.00,
            date: '2024-01-19',
            status: 'pending',
          },
          {
            id: 3,
            type: 'sale' as const,
            amount: 850.00,
            date: '2024-01-19',
            status: 'completed',
          },
        ],
      };
    }
  },

  async getPOSTransactions(params?: Record<string, any>): Promise<any[]> {
    const res = await dashboardAxios.get<any[]>('/pos/transactions', { params });
    return res.data;
  },

  async getInventoryOverview(params?: Record<string, any>): Promise<any> {
    const res = await dashboardAxios.get('/inventory-views/dashboard/inventory-overview', { params });
    return res.data;
  },

  async getStockDetailed(params?: Record<string, any>): Promise<any> {
    const res = await dashboardAxios.get('/inventory-views/stock/detailed', { params });
    return res.data;
  },

  // Get real-time dashboard updates
  async getRealtimeStats(): Promise<any> {
    try {
      const res = await dashboardAxios.get('/dashboard/realtime');
      return res.data;
    } catch (error) {
      console.error('Error fetching realtime stats:', error);
      throw error;
    }
  },

  // Get sales analytics
  async getSalesAnalytics(period: string = '30d'): Promise<any> {
    const res = await dashboardAxios.get('/analytics/sales', { params: { period } });
    return res.data;
  },

  // Get inventory analytics
  async getInventoryAnalytics(): Promise<any> {
    const res = await dashboardAxios.get('/analytics/inventory');
    return res.data;
  },

  // Get low stock alerts
  async getLowStockAlerts(): Promise<any[]> {
    try {
      const res = await dashboardAxios.get('/analytics/low-stock');
      return res.data;
    } catch (error) {
      console.error('Error fetching low stock alerts:', error);
      // Fallback to empty data when endpoint is not available
      return [];
    }
  },
};